import { StrictMode, useEffect } from 'react'
import ReactDOM from 'react-dom/client'

import { RouterProvider } from '@tanstack/react-router'

import './styles.css'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { GoogleOAuthProvider } from '@react-oauth/google'
import reportWebVitals from './reportWebVitals.ts'

import router from './pages/router.tsx'
import {
  LoginContext,
  isSessionExpired,
  loginWithGoogle,
  useUser,
} from './lib/auth.ts'
import { $client } from './lib/fetch.ts'
import { ToastProvider, toast } from './components/ui'
import type { LoginContextValue } from './lib/auth.ts'

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}

function RootRoute() {
  const [currentUser, setCurrentUser] = useUser()
  const logout = () => setCurrentUser(null)

  // Check expiry token.
  useEffect(() => {
    if (!currentUser) return

    const interval = setInterval(() => {
      if (isSessionExpired(currentUser)) {
        logout()
      }
    }, 30 * 1000) // check every 30 seconds

    return () => clearInterval(interval)
  }, [currentUser, setCurrentUser])

  const loginContext: LoginContextValue = {
    user: currentUser,
    logout: async () => {
      await $client.POST('/api/auth/logout')
      logout()
      router.navigate({ to: '/' })
      toast({
        color: 'primary',
        title: 'Déconnecté avec succès !',
      })
    },
    login: setCurrentUser,
    logOAuth: loginWithGoogle(setCurrentUser),
  }

  return (
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <GoogleOAuthProvider
          clientId={import.meta.env.VITE_GOOGLE_AUTH_CLIENT_ID}
        >
          <LoginContext.Provider value={loginContext}>
            <RouterProvider
              router={router}
              context={{ user: loginContext.user }}
            />
            <ToastProvider />
          </LoginContext.Provider>
        </GoogleOAuthProvider>
      </QueryClientProvider>
    </StrictMode>
  )
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (_, error) => (error as any)?.status !== 404,
    },
  },
})
const rootElement = document.getElementById('app')
if (rootElement && !rootElement.innerHTML) {
  const root = ReactDOM.createRoot(rootElement)
  root.render(<RootRoute />)
  document.getElementById('initial-loader')?.remove()
}

reportWebVitals()
